import type {
  CrossRelation, OhaengHarmony, YongSinCompatibility, GuiinMatch,
  NapeumCompat, ZodiacCompat, SpousePalace, FortuneFlowCompat,
  StarCross, ChildrenFortune, JohooCompat, StrengthCross,
  GyeokGukCompat, TwelveStageCross,
  MarriagePotential, TraditionalChecklist,
  RelationshipType, GradeInput,
  CompatibilityGrade, DimensionScore, GradeResult, Intensity,
} from '../types/compatibility';
import { getBranchElement, getElementRelationship } from '../core/five-elements';
import type { SajuAnalysis } from '../types/input';
import type { TenGod } from '../types/ten-gods';
import { branchIndex } from '../core/sexagenary';
import { getStemEumyang } from '../core/eumyang';
import { getGongmang } from '../sinsal/definitions';

/** 한 사주에서 특정 십신의 천간 출현 수를 센다 (일간 제외) */
function countTenGod(analysis: SajuAnalysis, targets: TenGod[]): number {
  const tg = analysis.tenGods;
  let count = 0;
  if (targets.includes(tg.year.tenGod)) count++;
  if (targets.includes(tg.month.tenGod)) count++;
  if (tg.hour && targets.includes(tg.hour.tenGod)) count++;
  return count;
}

const BIGYEOP: TenGod[] = ['비견', '겁재'];
const SIKSANG: TenGod[] = ['식신', '상관'];
const JAESEONG: TenGod[] = ['편재', '정재'];
const GWANSEONG: TenGod[] = ['편관', '정관'];

/**
 * 한 사주에서 재성/관성(배우자 별)이 위치한 기둥 중, 해당 기둥의 지지가
 * partner의 공망 집합에 포함되는 개수를 센다. 천간 십신 + 지장간 십신을 모두 고려.
 * 기둥 단위로 한 번만 카운트한다.
 *
 * 자평 전통: 배우자 별은 성별에 따라 다름 — 남자는 재성, 여자는 관성
 * (marriage-potential.ts의 gender-aware 로직과 일관)
 */
function countJaegwanInGongmang(analysis: SajuAnalysis, partnerGongmang: Set<number>): number {
  const { tenGods: tg, fourPillars: fp, input } = analysis;
  const targets = input.gender === 'male' ? JAESEONG : GWANSEONG;

  const positions: Array<{ pos: 'year' | 'month' | 'day' | 'hour'; branch: number }> = [
    { pos: 'year', branch: branchIndex(fp.year.branch) },
    { pos: 'month', branch: branchIndex(fp.month.branch) },
    { pos: 'day', branch: branchIndex(fp.day.branch) },
  ];
  // noHour 모드에서는 tg.hour / tg.hiddenStems.hour가 undefined이므로 시주 자리는 제외
  // (fp.hour는 placeholder로 항상 존재하므로 신뢰할 수 없음)
  if (tg.hour) positions.push({ pos: 'hour', branch: branchIndex(fp.hour.branch) });

  let count = 0;
  for (const { pos, branch } of positions) {
    if (!partnerGongmang.has(branch)) continue;
    // 천간 십신 (일간 제외)
    const stemGod = pos === 'year' ? tg.year.tenGod
      : pos === 'month' ? tg.month.tenGod
      : pos === 'hour' ? tg.hour?.tenGod
      : null;
    const hasJaegwan = (stemGod !== null && stemGod !== undefined && targets.includes(stemGod))
      || (tg.hiddenStems[pos]?.some(h => targets.includes(h.tenGod)) ?? false);
    if (hasJaegwan) count++;
  }
  return count;
}

const GRADE_LABELS: Record<CompatibilityGrade, string> = {
  '上上': '천생연분',
  '上': '매우 좋은 궁합',
  '中上': '좋은 궁합',
  '中': '무난한 궁합',
  '中下': '다소 어려운 궁합',
  '下': '어려운 궁합',
  '下下': '매우 어려운 궁합',
};

/** 관계 유형별 차원 가중치 [끌림, 소통, 조화, 안정, 발전] */
const DIMENSION_WEIGHTS: Record<RelationshipType, number[]> = {
  romantic:  [0.15, 0.20, 0.20, 0.25, 0.20],  // 소통 상향, 끌림 하향 (장기 관계에서 소통이 끌림보다 중요)
  friend:    [0.10, 0.30, 0.25, 0.25, 0.10],
  business:  [0.05, 0.25, 0.25, 0.25, 0.20],  // 발전 상향, 조화 하향 (사업은 미래 시너지가 핵심)
  family:    [0.05, 0.20, 0.25, 0.30, 0.20],  // 안정 상향, 끌림 하향 (가족은 안정이 최우선, 끌림 무의미)
};

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

/**
 * Sigmoid 압축: 극단값(1, 10)에 몰리는 현상을 완화한다.
 * 중심(5.5) 부근은 거의 변하지 않고, 양 극단으로 갈수록 압축.
 */
function softClamp(raw: number): number {
  const mid = 5.5;
  const halfRange = 4.5;
  const normalized = (raw - mid) / halfRange;
  const compressed = mid + halfRange * Math.tanh(normalized * 0.85);
  return clamp(compressed, 1, 10);
}

/** rawScore(원본) + score(압축 후 정수)를 포함한 DimensionScore를 만든다. */
function makeDimScore(
  key: DimensionScore['key'], label: string, raw: number, factors: string[],
): DimensionScore {
  const compressed = softClamp(raw);
  return { key, label, score: Math.round(compressed), rawScore: Math.round(raw * 100) / 100, factors };
}

/**
 * 같은 기둥 쌍에 합(긍정)과 충(부정)이 동시 존재하는 쌍 수를 세어
 * 감쇠 적용할 팩터 수를 반환한다. 양쪽에서 상쇄되는 만큼 총점에서 감쇠.
 */
function countConflictedPairs(crossRelations: CrossRelation[]): number {
  const pairKey = (r: CrossRelation) => [r.person1Pillar, r.person2Pillar].sort().join('-');
  const pairPositive = new Set<string>();
  const pairNegative = new Set<string>();
  for (const r of crossRelations) {
    const k = pairKey(r);
    // 지장간합은 수가 과다하여 감쇠 왜곡 방지를 위해 제외 (INTENSITY_POSITIVE 사용)
    if (INTENSITY_POSITIVE.has(r.type)) pairPositive.add(k);
    if (INTENSITY_NEGATIVE.has(r.type)) pairNegative.add(k);
  }
  let count = 0;
  for (const k of pairPositive) {
    if (pairNegative.has(k)) count++;
  }
  return count;
}

const GRADE_THRESHOLDS: [number, CompatibilityGrade][] = [
  [80, '上上'], [68, '上'], [56, '中上'], [44, '中'], [32, '中下'], [20, '下'],
];

function scoreToGrade(score: number): CompatibilityGrade {
  for (const [threshold, grade] of GRADE_THRESHOLDS) {
    if (score >= threshold) return grade;
  }
  return '下下';
}

// ── 끌림: 정서적 끌림과 매력 ──
function scoreAttraction(
  crossRelations: CrossRelation[],
  ohaengHarmony: OhaengHarmony,
  starCross: StarCross,
  johooCompat: JohooCompat,
  twelveStageCross: TwelveStageCross,
  type: RelationshipType,
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): DimensionScore {
  let s = 4;
  const factors: string[] = [];

  if (crossRelations.some(r => r.isDayPillar && r.type === '천간합')) {
    s += 3; factors.push('일간 천간합');
  }

  if (starCross.doHwaCross) {
    s += 1; factors.push('도화 교차');
  }

  // 암합: 발동률이 높으므로 존재 여부만 반영 (+1)
  const hasAmhap = crossRelations.some(r => r.type === '암합');
  if (hasAmhap) {
    s += 1; factors.push('암합');
  }

  if (type === 'romantic') {
    const ey1 = getStemEumyang(analysis1.dayMaster);
    const ey2 = getStemEumyang(analysis2.dayMaster);
    if (ey1 !== ey2) { s += 1; factors.push('음양 조화'); }
    else { s -= 1; factors.push('음양 동일'); }
  }

  const ohRel = ohaengHarmony.dayMasterRelation;
  if (ohRel === 'generates' || ohRel === 'generated') {
    s += 1; factors.push('일간 상생');
  } else if (ohRel === 'overcomes' || ohRel === 'overcome') {
    s -= 1; factors.push('일간 상극');
  }

  if (crossRelations.some(r => r.isDayPillar && r.type === '원진')) {
    s -= 1; factors.push('일지 원진');
  }

  // 조후 보완: 극단 기후에서 서로의 조후를 채워주면 강한 끌림
  if (johooCompat.harmony === '완벽보완') {
    s += 2; factors.push('조후 완벽보완');
  } else if (johooCompat.harmony === '부분보완') {
    s += 1; factors.push('조후 부분보완');
  } else if (johooCompat.harmony === '역조후') {
    s -= 1; factors.push('조후 역행');
  }

  // 십이운성: 상대 일지에서 강운성이면 끌림 증가
  if (twelveStageCross.pattern === '왕약상보') {
    s += 1; factors.push('운성 상보 끌림');
  }

  return makeDimScore('attraction', '끌림', s, factors);
}

// ── 소통: 성격 조화와 대화 방식 ──
function scoreCommunication(
  crossRelations: CrossRelation[],
  ohaengHarmony: OhaengHarmony,
  napeumCompat: NapeumCompat,
  strengthCross: StrengthCross,
  gyeokGukCompat: GyeokGukCompat,
  type: RelationshipType,
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): DimensionScore {
  let s = 4;
  const factors: string[] = [];

  const ohRel = ohaengHarmony.dayMasterRelation;
  if (ohRel === 'generates' || ohRel === 'generated') {
    s += 2; factors.push('일간 상생');
  } else if (ohRel === 'same') {
    s += 1; factors.push('일간 비화');
  } else if (ohRel === 'overcomes' || ohRel === 'overcome') {
    s -= 2; factors.push('일간 상극');
  }

  const nonDayStemCombines = crossRelations.filter(r =>
    !r.isDayPillar && r.type === '천간합'
  ).length;
  if (nonDayStemCombines > 0) {
    s += Math.min(nonDayStemCombines * 0.5, 1.5);
    factors.push(`비일주 천간합 ${nonDayStemCombines}개`);
  }

  const nonDayStemClashes = crossRelations.filter(r =>
    !r.isDayPillar && r.type === '천간충'
  ).length;
  if (nonDayStemClashes > 0) {
    s -= Math.min(nonDayStemClashes * 0.5, 1.5);
    factors.push(`비일주 천간충 ${nonDayStemClashes}개`);
  }

  if (crossRelations.some(r => r.isDayPillar && r.type === '천간충')) {
    s -= 2; factors.push('일주 천간충');
  }

  if (ohaengHarmony.distributionBalance >= 0.4) {
    s += 1; factors.push('오행 보완');
  }

  const napRel = napeumCompat.relation;
  if (napRel === 'generates' || napRel === 'generated') {
    s += 1; factors.push('납음 상생');
  } else if (napRel === 'overcomes' || napRel === 'overcome') {
    s -= 1; factors.push('납음 상극');
  }

  // 신강/신약 교차: 강-강은 소통 갈등, 강-약은 자연스러운 역할 분담
  if (strengthCross.pattern === '강강') {
    s -= 1; factors.push('강강 충돌');
  } else if (strengthCross.pattern === '강약') {
    s += 1; factors.push('강약 상보');
  }

  // 관계 유형별 고유 보너스
  if (type === 'friend') {
    // 비견/겁재가 양쪽에 있으면 동류의식 → 소통 향상
    if (countTenGod(analysis1, BIGYEOP) > 0 && countTenGod(analysis2, BIGYEOP) > 0) {
      s += 1; factors.push('비겁 공감');
    }
    // 일간 비화는 친구에서 특히 유리 (이미 +1이지만 추가 +1)
    if (ohaengHarmony.dayMasterRelation === 'same') {
      s += 1; factors.push('동오행 공감');
    }
  } else if (type === 'business') {
    // 한쪽에 관성, 다른쪽에 재성 → 구조적 소통 (지시-실행 흐름)
    const p1Gwan = countTenGod(analysis1, GWANSEONG);
    const p2Jae = countTenGod(analysis2, JAESEONG);
    const p2Gwan = countTenGod(analysis2, GWANSEONG);
    const p1Jae = countTenGod(analysis1, JAESEONG);
    if ((p1Gwan > 0 && p2Jae > 0) || (p2Gwan > 0 && p1Jae > 0)) {
      s += 1; factors.push('재관 소통');
    }
  }

  // 격국 호환: 십신 흐름이 맞으면 소통 스타일 합치
  if (gyeokGukCompat.synergy === '상생흐름') {
    s += 1; factors.push('격국 상생흐름');
  } else if (gyeokGukCompat.synergy === '충돌') {
    s -= 1; factors.push('격국 충돌');
  }

  return makeDimScore('communication', '소통', s, factors);
}

// ── 조화: 내면적 균형과 합의 힘 ──
function scoreHarmony(
  crossRelations: CrossRelation[],
  yongSinCompat: YongSinCompatibility,
  guiinMatches: GuiinMatch[],
  zodiacCompat: ZodiacCompat,
): DimensionScore {
  let s = 3;
  const factors: string[] = [];

  // 일지 합 (육합/삼합)
  if (crossRelations.some(r => r.isDayPillar && (r.type === '육합' || r.type === '삼합'))) {
    s += 3; factors.push('일지 합');
  }

  // 비일주 합: 육합/삼합은 강, 반합/방합은 약
  const strongCombines = crossRelations.filter(r =>
    !r.isDayPillar && (r.type === '육합' || r.type === '삼합')
  ).length;
  const weakCombines = crossRelations.filter(r =>
    !r.isDayPillar && (r.type === '반합' || r.type === '방합')
  ).length;
  const combineScore = Math.min(strongCombines * 0.5 + weakCombines * 0.3, 2);
  if (combineScore > 0) {
    s += combineScore;
    factors.push(`비일주 합 ${strongCombines + weakCombines}개`);
  }

  // 용신 호환
  if (yongSinCompat.mutualSupport === '상호보완') {
    s += 2; factors.push('용신 상호보완');
  } else if (yongSinCompat.mutualSupport === '부분보완') {
    s += 1; factors.push('용신 부분보완');
  } else if (yongSinCompat.mutualSupport === '중립') {
    s -= 1; factors.push('용신 중립');
  } else if (yongSinCompat.mutualSupport === '충돌') {
    s -= 2; factors.push('용신 충돌');
  }

  // 귀인
  const mutualTypes = new Set(guiinMatches.filter(g => g.mutual).map(g => g.type));
  if (mutualTypes.size > 0) {
    s += mutualTypes.size * 2;
    factors.push(`상호귀인 ${mutualTypes.size}종`);
  }
  if (guiinMatches.length >= 2) {
    s += 1; factors.push(`귀인 ${guiinMatches.length}개`);
  }

  // 띠 육합/육충
  if (zodiacCompat.relations.some(r => r.type === '육합')) {
    s += 1; factors.push('띠 육합');
  } else if (zodiacCompat.relations.some(r => r.type === '육충')) {
    s -= 1; factors.push('띠 육충');
  }

  return makeDimScore('harmony', '조화', s, factors);
}

// ── 안정: 관계의 안정성 (갈등 요소 역산, 높을수록 안정) ──
function scoreStability(
  crossRelations: CrossRelation[],
  zodiacCompat: ZodiacCompat,
  strengthCross: StrengthCross,
  twelveStageCross: TwelveStageCross,
  isTraditional: boolean,
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): DimensionScore {
  let s = 9;
  const factors: string[] = [];
  // traditional 모드: 신살(원진/귀문/형/파/해) 감점 50% 축소
  const sinsalMul = isTraditional ? 0.5 : 1.0;

  // 일지 육충 (합충형파해 코어 → 모드 무관)
  if (crossRelations.some(r => r.isDayPillar && r.type === '육충')) {
    s -= 3; factors.push('일지 육충');
  }
  // 일지 형 (신살 성격)
  if (crossRelations.some(r => r.isDayPillar && (r.type === '삼형' || r.type === '상형' || r.type === '자묘형'))) {
    s -= 2 * sinsalMul; factors.push('일지 형살');
  }
  // 일지 파
  if (crossRelations.some(r => r.isDayPillar && r.type === '파')) {
    s -= 1 * sinsalMul; factors.push('일지 파');
  }
  // 일지 해
  if (crossRelations.some(r => r.isDayPillar && r.type === '해')) {
    s -= 1 * sinsalMul; factors.push('일지 해');
  }

  // 비일주 육충 (년지-년지는 띠 육충에서 별도 계산하므로 제외)
  const nonDayClashes = crossRelations.filter(r =>
    !r.isDayPillar && r.type === '육충' &&
    !(r.person1Pillar === 'year' && r.person2Pillar === 'year')
  ).length;
  if (nonDayClashes > 0) {
    s -= Math.min(nonDayClashes * 0.5, 1.5);
    factors.push(`비일주 육충 ${nonDayClashes}개`);
  }

  // 일지 원진 (신살)
  if (crossRelations.some(r => r.isDayPillar && r.type === '원진')) {
    s -= 1.5 * sinsalMul; factors.push('일지 원진');
  }
  // 일지 귀문 (신살)
  if (crossRelations.some(r => r.isDayPillar && r.type === '귀문')) {
    s -= 1 * sinsalMul; factors.push('일지 귀문');
  }

  // 비일주 원진 (신살)
  const nonDayWonjin = crossRelations.filter(r => !r.isDayPillar && r.type === '원진').length;
  if (nonDayWonjin > 0) {
    s -= Math.min(nonDayWonjin * 0.5 * sinsalMul, 1 * sinsalMul);
    factors.push(`비일주 원진 ${nonDayWonjin}개`);
  }
  // 비일주 귀문 (신살)
  const nonDayGuimun = crossRelations.filter(r => !r.isDayPillar && r.type === '귀문').length;
  if (nonDayGuimun > 0) {
    s -= Math.min(nonDayGuimun * 0.5 * sinsalMul, 1 * sinsalMul);
    factors.push(`비일주 귀문 ${nonDayGuimun}개`);
  }

  // 띠 육충
  if (zodiacCompat.relations.some(r => r.type === '육충')) {
    s -= 1; factors.push('띠 육충');
  }

  // 공망 교차 (신살)
  const [gm1a, gm1b] = getGongmang(analysis1.fourPillars.day.sexagenaryIndex);
  const [gm2a, gm2b] = getGongmang(analysis2.fourPillars.day.sexagenaryIndex);
  const gm1 = new Set([gm1a, gm1b]);
  const gm2 = new Set([gm2a, gm2b]);
  const dayB1 = branchIndex(analysis1.fourPillars.day.branch);
  const dayB2 = branchIndex(analysis2.fourPillars.day.branch);
  if (gm1.has(dayB2) || gm2.has(dayB1)) {
    s -= 1 * sinsalMul; factors.push('공망 교차');
  }

  // 공망-재관 교차: 내 재성/관성이 위치한 기둥의 지지가 상대 공망에 들면
  // 배우자 별이 "비어있는" 자리에 놓이는 것으로, 배우자 리스크 요인
  const jaegwanCross1 = countJaegwanInGongmang(analysis1, gm2);
  const jaegwanCross2 = countJaegwanInGongmang(analysis2, gm1);
  const jaegwanCross = jaegwanCross1 + jaegwanCross2;
  if (jaegwanCross > 0) {
    s -= Math.min(jaegwanCross * 0.5, 1.5) * sinsalMul;
    factors.push(`공망-재관 교차 ${jaegwanCross}개`);
  }

  // 신강/신약: 약약은 안정 저하, 강강은 다툼 위험
  if (strengthCross.pattern === '약약') {
    s -= 1; factors.push('약약 불안정');
  } else if (strengthCross.pattern === '강강') {
    s -= 1; factors.push('강강 마찰');
  } else if (strengthCross.pattern === '강약') {
    s += 1; factors.push('강약 안정');
  }

  // 삼합/방합: 구조적 결속 → 안정성 향상
  const structuralCombines = crossRelations.filter(r =>
    r.type === '삼합' || r.type === '방합'
  ).length;
  if (structuralCombines > 0) {
    const bonus = Math.min(structuralCombines * 0.5, 1.5);
    s += bonus;
    factors.push(`삼합/방합 결속 ${structuralCombines}개`);
  }

  // 십이운성: 쌍약이면 안정 저하, 왕약상보이면 안정 향상
  if (twelveStageCross.pattern === '쌍약') {
    s -= 1; factors.push('운성 쌍약');
  } else if (twelveStageCross.pattern === '왕약상보') {
    s += 1; factors.push('운성 상보');
  }

  return makeDimScore('stability', '안정', s, factors);
}

// ── 발전: 장기적 전망과 성장 ──
function scoreGrowth(
  crossRelations: CrossRelation[],
  fortuneFlow: FortuneFlowCompat,
  spousePalace: SpousePalace,
  childrenFortune: ChildrenFortune,
  starCross: StarCross,
  ohaengHarmony: OhaengHarmony,
  johooCompat: JohooCompat,
  gyeokGukCompat: GyeokGukCompat,
  type: RelationshipType,
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): DimensionScore {
  let s = 3;
  const factors: string[] = [];

  // 대운 + 세운 블렌딩 시너지 (대운 70%, 세운 30%)
  const blendedSynergy = fortuneFlow.yearlySynergyScore !== undefined
    ? fortuneFlow.synergyScore * 0.7 + fortuneFlow.yearlySynergyScore * 0.3
    : fortuneFlow.synergyScore;

  if (blendedSynergy >= 0.8) {
    s += 3; factors.push('운세 시너지 매우좋음');
  } else if (blendedSynergy >= 0.6) {
    s += 2; factors.push('운세 시너지 좋음');
  } else if (blendedSynergy >= 0.4) {
    s += 1; factors.push('운세 시너지 보통');
  } else {
    s -= 1; factors.push('운세 시너지 주의');
  }

  // 배우자궁 (romantic/family)
  if (type === 'romantic' || type === 'family') {
    if (spousePalace.harmony === '매우좋음') {
      s += 2; factors.push('배우자궁 매우좋음');
    } else if (spousePalace.harmony === '좋음') {
      s += 1; factors.push('배우자궁 좋음');
    } else if (spousePalace.harmony === '주의') {
      s -= 2; factors.push('배우자궁 주의');
    }
  }

  // 자녀운 (romantic/family)
  if (type === 'romantic' || type === 'family') {
    if (childrenFortune.compatibility === '조화') {
      s += 1; factors.push('자녀운 조화');
    } else if (childrenFortune.compatibility === '부족') {
      s -= 1; factors.push('자녀운 부족');
    }
  }

  // 역마 교차
  if (starCross.yeokMaCross) {
    s += 1; factors.push('역마 교차');
  }

  // 오행 보완 (business/friend)
  if (type === 'business' || type === 'friend') {
    if (ohaengHarmony.distributionBalance >= 0.4) {
      s += 1; factors.push('오행 상호보완');
    }
  }

  // 조후 보완이 있으면 장기적 성장에도 긍정적
  if (johooCompat.harmony === '완벽보완') {
    s += 1; factors.push('조후 장기 시너지');
  } else if (johooCompat.harmony === '역조후') {
    s -= 1; factors.push('조후 역행 리스크');
  }

  // 관계 유형별 고유 보너스
  if (type === 'friend') {
    // 양쪽 모두 식상(식신/상관)이 있으면 함께 즐기는 활동 풍부
    if (countTenGod(analysis1, SIKSANG) > 0 && countTenGod(analysis2, SIKSANG) > 0) {
      s += 1; factors.push('식상 교차');
    }
  } else if (type === 'business') {
    // 재관 시너지: 한쪽 재성 + 다른쪽 관성 → 부(富)와 권(權)의 흐름
    const p1Jae = countTenGod(analysis1, JAESEONG);
    const p2Gwan = countTenGod(analysis2, GWANSEONG);
    const p2Jae = countTenGod(analysis2, JAESEONG);
    const p1Gwan = countTenGod(analysis1, GWANSEONG);
    if ((p1Jae > 0 && p2Gwan > 0) || (p2Jae > 0 && p1Gwan > 0)) {
      s += 1; factors.push('재관 시너지');
    }
    // 양쪽 모두 재성 보유 → 사업 확장력
    if (p1Jae > 0 && p2Jae > 0) {
      s += 1; factors.push('재성 쌍보유');
    }
  }

  // 삼합 결과 오행이 용신이면 장기 성장에 유리
  const yongSin1 = analysis1.yongSinMethods[0]?.yongSin;
  const yongSin2 = analysis2.yongSinMethods[0]?.yongSin;
  const samhapResults = crossRelations
    .filter(r => r.type === '삼합' && r.result)
    .map(r => r.result!);
  if (samhapResults.some(oh => oh === yongSin1 || oh === yongSin2)) {
    s += 1; factors.push('삼합 용신 연계');
  }

  // 격국 시너지: 상생흐름은 장기 성장에 유리
  if (gyeokGukCompat.synergy === '상생흐름') {
    s += 1; factors.push('격국 상생 발전');
  } else if (gyeokGukCompat.synergy === '충돌') {
    s -= 1; factors.push('격국 충돌 리스크');
  }

  return makeDimScore('growth', '발전', s, factors);
}

/**
 * 차원별 궁합 판정.
 *
 * 5개 차원(끌림/소통/조화/안정/발전)을 각각 1-10점으로 평가하고,
 * 관계 유형별 가중 평균으로 총점(0-100)과 등급을 산출한다.
 */
export function determineGrade(input: GradeInput): GradeResult {
  const {
    crossRelations, ohaengHarmony, yongSinCompat, guiinMatches,
    napeumCompat, zodiacCompat, spousePalace, fortuneFlow,
    starCross, childrenFortune, johooCompat, strengthCross,
    gyeokGukCompat, twelveStageCross, spouseRole, sangsaengji,
    compatMode, marriagePotential,
    type, analysis1, analysis2,
  } = input;

  const isTraditional = compatMode === 'traditional';

  let dimensions: DimensionScore[] = [
    scoreAttraction(crossRelations, ohaengHarmony, starCross, johooCompat, twelveStageCross, type, analysis1, analysis2),
    scoreCommunication(crossRelations, ohaengHarmony, napeumCompat, strengthCross, gyeokGukCompat, type, analysis1, analysis2),
    scoreHarmony(crossRelations, yongSinCompat, guiinMatches, zodiacCompat),
    scoreStability(crossRelations, zodiacCompat, strengthCross, twelveStageCross, isTraditional, analysis1, analysis2),
    scoreGrowth(crossRelations, fortuneFlow, spousePalace, childrenFortune, starCross, ohaengHarmony, johooCompat, gyeokGukCompat, type, analysis1, analysis2),
  ];

  // ── 격국/용신 위계 캡 (traditional 모드) ──
  if (isTraditional) {
    if (yongSinCompat.mutualSupport === '충돌') {
      // 용신 충돌 시 모든 차원 rawScore 상한 7.0
      dimensions = dimensions.map(d => ({
        ...d,
        rawScore: Math.min(d.rawScore, 7.0),
        score: Math.min(d.score, 7),
        factors: [...d.factors, '용신충돌 상한'],
      }));
    } else if (yongSinCompat.mutualSupport === '상호보완') {
      // 용신 상호보완 시 모든 차원 rawScore 하한 3.0
      dimensions = dimensions.map(d => ({
        ...d,
        rawScore: Math.max(d.rawScore, 3.0),
        score: Math.max(d.score, 3),
      }));
    }
  }

  // ── 육친 역할 반영 (끌림/소통) ──
  const attractionIdx = dimensions.findIndex(d => d.key === 'attraction');
  const commIdx = dimensions.findIndex(d => d.key === 'communication');
  if (spouseRole.roleHarmony === '이상적') {
    dimensions[attractionIdx] = addFactor(dimensions[attractionIdx], 1, '육친 이상적');
    dimensions[commIdx] = addFactor(dimensions[commIdx], 1, '육친 이상적');
  } else if (spouseRole.roleHarmony === '주의') {
    dimensions[attractionIdx] = addFactor(dimensions[attractionIdx], -0.5, '육친 주의');
    dimensions[commIdx] = addFactor(dimensions[commIdx], -0.5, '육친 주의');
  }

  // ── 상생지 교차 반영 (조화) ──
  const harmonyIdx = dimensions.findIndex(d => d.key === 'harmony');
  if (sangsaengji.harmony === '매우좋음') {
    dimensions[harmonyIdx] = addFactor(dimensions[harmonyIdx], 1, '상생지 매우좋음');
  } else if (sangsaengji.harmony === '좋음') {
    dimensions[harmonyIdx] = addFactor(dimensions[harmonyIdx], 0.5, '상생지 좋음');
  }

  const weights = DIMENSION_WEIGHTS[type];
  // rawScore 기반으로 총점 계산 (소수점 정밀도)
  let totalScore = dimensions.reduce((sum, d, i) => sum + d.rawScore * weights[i], 0) * 10;

  // 팩터 상호작용 감쇠: 같은 기둥 쌍에 합+충 공존 시 총점 감쇠 (쌍당 -1.5점)
  const conflicted = countConflictedPairs(crossRelations);
  if (conflicted > 0) {
    totalScore -= conflicted * 1.5;
  }

  // ── 혼인 그릇 보정 (traditional 모드에서만 총점 보정, standard는 keyFactors에만 표시) ──
  // 감점 방식: 리스크 개수 × 2점 감점 (최대 10점), 곱하기 대신 빼기로 과도한 감점 방지
  if (marriagePotential && isTraditional) {
    const riskCount =
      marriagePotential.person1.riskFlags.length +
      marriagePotential.person1.spousePalaceConflicts.length +
      marriagePotential.person2.riskFlags.length +
      marriagePotential.person2.spousePalaceConflicts.length;
    totalScore -= Math.min(riskCount * 2, 10);
  }

  const score = clamp(Math.round(totalScore), 0, 100);
  const grade = scoreToGrade(score);

  // Intensity 계산: 합(긍정)과 충(부정)이 동시에 많을수록 강렬
  const intensity = calculateIntensity(crossRelations);

  // 핵심 요인
  const keyFactors: string[] = [];
  const dimStrengths = dimensions.filter(d => d.score >= 7).sort((a, b) => b.score - a.score);
  const dimWeaknesses = dimensions.filter(d => d.score <= 4).sort((a, b) => a.score - b.score);
  if (dimStrengths.length > 0) {
    keyFactors.push(`강점: ${dimStrengths.map(d => `${d.label}(${d.score})`).join(', ')}`);
  }
  if (dimWeaknesses.length > 0) {
    keyFactors.push(`주의: ${dimWeaknesses.map(d => `${d.label}(${d.score})`).join(', ')}`);
  }
  if (intensity.level === '강렬' || intensity.level === '극적') {
    keyFactors.push(`강렬도: ${intensity.level}(${intensity.score})`);
  }
  if (marriagePotential) {
    const riskFlags = [
      ...marriagePotential.person1.riskFlags.map(f => `P1:${f}`),
      ...marriagePotential.person2.riskFlags.map(f => `P2:${f}`),
    ];
    if (riskFlags.length > 0) keyFactors.push(`혼인리스크: ${riskFlags.join(', ')}`);
  }
  if (keyFactors.length === 0) keyFactors.push('전반적으로 무난');

  // ── 전통 체크리스트 (traditional 모드) ──
  const traditionalChecklist = isTraditional
    ? buildTraditionalChecklist(crossRelations, ohaengHarmony, yongSinCompat, marriagePotential, analysis1, analysis2)
    : undefined;

  return {
    grade, score, dimensions, intensity, keyFactors,
    marriagePotential,
    traditionalChecklist,
  };
}

/** DimensionScore에 factor 추가하면서 rawScore/score 보정 */
function addFactor(dim: DimensionScore, delta: number, factor: string): DimensionScore {
  const newRaw = dim.rawScore + delta;
  return {
    ...dim,
    rawScore: Math.round(newRaw * 100) / 100,
    score: Math.round(softClamp(newRaw)),
    factors: [...dim.factors, factor],
  };
}

/** 전통 자평 체크리스트: 5개 핵심 항목 */
function buildTraditionalChecklist(
  crossRelations: CrossRelation[],
  ohaengHarmony: OhaengHarmony,
  yongSinCompat: YongSinCompatibility,
  marriagePotential: { person1: MarriagePotential; person2: MarriagePotential } | undefined,
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): TraditionalChecklist {
  // 1. 일지 조화: 오행 상생/동기 또는 육합/삼합 존재
  const dayBranchRel = getElementRelationship(
    getBranchElement(analysis1.fourPillars.day.branch), getBranchElement(analysis2.fourPillars.day.branch),
  );
  const dayBranchElementHarmony = dayBranchRel === 'same' || dayBranchRel === 'generates' || dayBranchRel === 'generated';
  const dayBranchCombine = crossRelations.some(r =>
    r.isDayPillar && (r.type === '육합' || r.type === '삼합'),
  );
  const dayBranchHarmony = dayBranchElementHarmony || dayBranchCombine;

  // 2. 일간 상생/합
  const dayMasterHarmony = ohaengHarmony.dayMasterRelation === 'same' ||
    ohaengHarmony.dayMasterRelation === 'generates' ||
    ohaengHarmony.dayMasterRelation === 'generated' ||
    crossRelations.some(r => r.isDayPillar && r.type === '천간합');

  // 3. 용신 보완
  const yongsinSupport = yongSinCompat.mutualSupport === '상호보완' || yongSinCompat.mutualSupport === '부분보완';

  // 4. 재관 반듯함
  const jaeGwanHealthy = marriagePotential
    ? (marriagePotential.person1.riskFlags.length === 0 && marriagePotential.person2.riskFlags.length === 0)
    : true;

  // 5. 일주 충/형 없음
  const noMajorConflict = !crossRelations.some(r =>
    r.isDayPillar && (r.type === '육충' || r.type === '삼형' || r.type === '상형' || r.type === '자묘형'),
  );

  const checks = [dayBranchHarmony, dayMasterHarmony, yongsinSupport, jaeGwanHealthy, noMajorConflict];
  const passCount = checks.filter(Boolean).length;

  const labels = ['일지조화', '일간조화', '용신보완', '재관건전', '일주무충'];
  const passedLabels = labels.filter((_, i) => checks[i]);
  const failedLabels = labels.filter((_, i) => !checks[i]);
  const description = `전통체크: ${passCount}/5 통과` +
    (passedLabels.length > 0 ? ` (○ ${passedLabels.join(',')})` : '') +
    (failedLabels.length > 0 ? ` (✗ ${failedLabels.join(',')})` : '');

  return { dayBranchHarmony, dayMasterHarmony, yongsinSupport, jaeGwanHealthy, noMajorConflict, passCount, description };
}

// 지장간합은 weight(정기=1.0, 여기/중기는 ratio 비례)로 부분 반영
const INTENSITY_POSITIVE = new Set(['천간합', '육합', '삼합', '반합', '방합', '암합']);
const INTENSITY_NEGATIVE = new Set(['천간충', '육충', '삼형', '상형', '자묘형', '파', '해', '원진', '귀문']);

/**
 * 관계 강렬도(Intensity)를 계산한다.
 *
 * 합(긍정)과 충(부정)이 동시에 많을수록 "다이나믹한 관계"로,
 * 단순 총점만으로는 표현할 수 없는 관계의 역동성을 나타낸다.
 * 일주 관계에 가중치 2배.
 *
 * 지장간합은 r.weight (0~1, 정기-정기=1.0)로 비례 반영하여 과다 카운트를 방지.
 */
function calculateIntensity(crossRelations: CrossRelation[]): Intensity {
  let positiveCount = 0;
  let negativeCount = 0;

  for (const r of crossRelations) {
    const positionMul = r.isDayPillar ? 2 : 1;
    if (r.type === '지장간합') {
      // 정기-정기(weight=1.0)는 기존 합과 동일, 여기-여기는 0.04 수준
      positiveCount += positionMul * (r.weight ?? 0);
    } else if (INTENSITY_POSITIVE.has(r.type)) {
      positiveCount += positionMul;
    } else if (INTENSITY_NEGATIVE.has(r.type)) {
      negativeCount += positionMul;
    }
  }

  // 강렬도 = min(긍정, 부정) 기반, 절대 볼륨에 비례
  // 양쪽 모두 존재해야 강렬, 한쪽만 높으면 평탄/보통
  // 극적(8+) = overlap 9+, 강렬(5+) = overlap 4+, 보통(2+) = overlap 1+
  const overlap = Math.min(positiveCount, negativeCount);
  const score = clamp(Math.round(Math.sqrt(overlap) * 2.5), 0, 10);

  let level: Intensity['level'];
  if (score >= 8) level = '극적';
  else if (score >= 5) level = '강렬';
  else if (score >= 2) level = '보통';
  else level = '평탄';

  const parts: string[] = [`합 ${positiveCount}`, `충 ${negativeCount}`];
  const description = `강렬도: ${level} (${parts.join(', ')})`;

  return { positiveCount, negativeCount, score, level, description };
}

/**
 * 궁합 요약 문자열을 생성한다.
 */
export function generateSummary(
  gradeResult: GradeResult,
  type: RelationshipType,
): string {
  const { grade, score, dimensions, intensity } = gradeResult;

  const typeLabel: Record<RelationshipType, string> = {
    romantic: '연인', friend: '친구', business: '직장/사업', family: '가족',
  };

  const dimStr = dimensions.map(d => `${d.label} ${d.score}`).join(' · ');
  const intensityStr = intensity.level !== '평탄' ? ` | 강렬도 ${intensity.level}` : '';

  return `[${typeLabel[type]}] ${grade} ${GRADE_LABELS[grade]} (${score}점) | ${dimStr}${intensityStr}`;
}
